"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bold, Code, Italic, Link as LinkIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface FloatingFormatMenuProps {
  /** Posición del menú en viewport. */
  position: { x: number; y: number } | null;
  /** Si hay texto seleccionado. */
  visible: boolean;
  /** Handlers de cada acción. */
  onBold: () => void;
  onItalic: () => void;
  onLink: () => void;
  onCode: () => void;
}

/**
 * Mini-toolbar flotante que aparece al seleccionar texto en el editor.
 * Posicionada arriba del caret. Estilo Notion.
 * Se renderiza en portal.
 */
export function FloatingFormatMenu({
  position,
  visible,
  onBold,
  onItalic,
  onLink,
  onCode,
}: FloatingFormatMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (!mounted || !visible || !position) return null;

  return createPortal(
    <div
      ref={ref}
      role="toolbar"
      aria-label="Formato de selección"
      style={{ left: position.x, top: position.y }}
      className="fixed z-[220] -translate-x-1/2 -translate-y-full mb-1 flex items-center gap-0.5 px-1.5 py-1 rounded-lg bg-foreground/90 backdrop-blur-md shadow-xl border border-foreground/20 animate-in fade-in zoom-in-95 duration-100"
    >
      <Button onClick={onBold} label="Negrita" shortcut="B">
        <Bold className="w-3.5 h-3.5" />
      </Button>
      <Button onClick={onItalic} label="Cursiva" shortcut="I">
        <Italic className="w-3.5 h-3.5" />
      </Button>
      <Button onClick={onLink} label="Enlace" shortcut="K">
        <LinkIcon className="w-3.5 h-3.5" />
      </Button>
      <Button onClick={onCode} label="Código" shortcut="E">
        <Code className="w-3.5 h-3.5" />
      </Button>
    </div>,
    document.body,
  );
}

function Button({
  onClick,
  label,
  shortcut,
  children,
}: {
  onClick: () => void;
  label: string;
  shortcut: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      onMouseDown={(e) => e.preventDefault()}
      title={`${label} (${shortcut})`}
      aria-label={label}
      className={cn(
        "p-1.5 rounded text-background/80 hover:text-background hover:bg-background/20 transition-colors",
      )}
    >
      {children}
    </button>
  );
}
